'use strict';

const libPictProvider = require('pict-provider');
const libProjectTypeComprehensionBuilder = require('./ProjectTypeComprehensionBuilder.js');
const libEntityPickerSearch = require('../services/Entity-Picker-Search.js');

// Mount points (the ProjectTypes / ContractTypes views lay these out; this provider fills them with hand-built HTML —
// the CertificationTypes idiom). Only one of the two views is mounted at a time (routes are exclusive), so they share
// the same mount ids.
const RAIL_MOUNT = '#PCT-Rail';
const RAIL_LIST_MOUNT = '#PCT-Rail-List';
const DETAIL_MOUNT = '#PCT-Detail';
const ALLOWED_PICKER_MOUNT = '#PCT-AllowedPicker';

// Kind config — Project vs Contract type. The provider is parameterized by this so ~85% of the behavior is shared.
const KINDS =
{
	Project:
	{
		Kind: 'Project', Entity: 'ProjectType', Plural: 'ProjectTypes', IDField: 'IDProjectType', GUIDField: 'GUIDProjectType',
		SaveMethod: 'saveProjectType', Label: 'project type', LabelPlural: 'Project Types', Route: '#/ProjectTypes',
		HasOrdinal: true, HasAllowedTypes: false, HasFunding: false, HasDeleted: false,
		ProtoEntity: 'Project', ProtoIDField: 'IDProject', ProtoSettingKey: 'PrototypeIDProject', ProtoTab: '/Project/Workspace',
		DatesRoute: '#/ProjectDates', DefaultDatesLabel: 'Default project dates',
	},
	Contract:
	{
		Kind: 'Contract', Entity: 'ContractType', Plural: 'ContractTypes', IDField: 'IDContractType', GUIDField: 'GUIDContractType',
		SaveMethod: 'saveContractType', Label: 'contract type', LabelPlural: 'Contract Types', Route: '#/ContractTypes',
		HasOrdinal: false, HasAllowedTypes: true, HasFunding: true, HasDeleted: true,
		ProtoEntity: 'Contract', ProtoIDField: 'IDContract', ProtoSettingKey: 'PrototypeIDContract', ProtoTab: '/Contract/Workspace',
		DatesRoute: '#/ContractDates', DefaultDatesLabel: 'Default contract dates',
	},
};

// Prototype "template" steps surfaced in the detail — each a button that deep-links into the existing first-class
// editor scoped to the prototype. `Scope:'prototype'` steps are truly scoped; `'org'` steps are org-wide editors.
const PROTO_STEPS =
[
	{ Key: 'dates', Label: 'Important dates', Kinds: [ 'Project', 'Contract' ], Scope: 'prototype', route: (pKindCfg, pProtoID) => `${pKindCfg.DatesRoute}/${pProtoID}` },
	{ Key: 'observations', Label: 'Observation types', Kinds: [ 'Project' ], Scope: 'prototype', route: (pKindCfg, pProtoID) => `#/Project/Workspace/${pProtoID}/observations` },
	{ Key: 'labels', Label: 'Labels', Kinds: [ 'Project' ], Scope: 'prototype', route: (pKindCfg, pProtoID) => `#/Project/Workspace/${pProtoID}/labels` },
	{ Key: 'sampling', Label: 'Sample plan', Kinds: [ 'Project' ], Scope: 'prototype', route: (pKindCfg, pProtoID) => `#/SamplePlan/${pProtoID}` },
	{ Key: 'documents', Label: 'Forms & documents', Kinds: [ 'Project' ], Scope: 'org', route: () => '#/ReportProjectAssignment' },
	{ Key: 'approvals', Label: 'Approval workflow', Kinds: [ 'Project', 'Contract' ], Scope: 'org', route: () => '#/ApprovalWorkflows/Bindings/Observation' },
	{ Key: 'funding', Label: 'Funding', Kinds: [ 'Project', 'Contract' ], Scope: 'org', route: () => '#/ContractAdmin' },
];

/**
 * ProjectContractTypes — the bespoke #/ProjectTypes and #/ContractTypes editors, replacing the generic PSRS forms.
 * One provider, parameterized by kind (state sliced per kind in AppData.ProjectContractTypes.{Project,Contract} so
 * navigating between the two never clobbers unsaved edits). Reuses the server-side machinery unchanged:
 * `TypeComprehensionSaveProvider` (save → prototype → comprehension → statuses/dates/allowed-types) and
 * `TypeComprehensionReadProvider` (hydrate statuses / default dates / allowed types / prototype). The prototype is
 * surfaced openly with deep-links into the existing first-class editors.
 */
class ProjectContractTypesProvider extends libPictProvider
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);
		this._fundKeySeq = 0;   // stable per-row key for the default-funding pickers (index shifts on add/remove)
	}

	get restClient() { return this.pict.fable.HeadlightRestClient || this.pict.HeadlightRestClient; }
	get _saveProvider() { return (this.pict.fable && this.pict.fable.TypeComprehensionSaveProvider) || this.fable.TypeComprehensionSaveProvider; }
	get _readProvider() { return (this.pict.fable && this.pict.fable.TypeComprehensionReadProvider) || this.fable.TypeComprehensionReadProvider; }
	callerCustomer() { const tmpUser = this.pict.AppData.CurrentUser; return tmpUser ? Number(tmpUser.CustomerID || tmpUser.IDCustomer) : 0; }

	// ── Capability gates (server authoritative; fail-open before the map loads) ───
	_can(pEntity, pAction)
	{
		const tmpApp = this.pict.fable;   // the EffectivePermissionsService lives on fable, not the app instance
		if (tmpApp && tmpApp.EffectivePermissionsService && (typeof tmpApp.EffectivePermissionsService.can === 'function')) { return tmpApp.EffectivePermissionsService.can(pEntity, pAction); }
		return true;
	}
	get _entity() { return this.cfg.Entity; }
	canCreate() { return this._can(this._entity, 'Create'); }
	canEditDef() { return this._can(this._entity, 'Update'); }
	canDelete() { return this._can(this._entity, 'Delete'); }
	canEditProto() { return this._can(this.cfg.ProtoEntity, 'Update'); }   // prototype is a real Project/Contract; gate on that kind's Update

	// ── State (sliced per kind) ──────────────────────────────────────────────────
	get root()
	{
		if (!this.pict.AppData.ProjectContractTypes)
		{
			this.pict.AppData.ProjectContractTypes = { ActiveKind: 'Project', Project: this._freshSlice(), Contract: this._freshSlice() };
		}
		return this.pict.AppData.ProjectContractTypes;
	}
	get state() { return this.root[this.root.ActiveKind]; }
	get cfg() { return KINDS[this.root.ActiveKind]; }

	_freshSlice()
	{
		return {
			Loaded: false, Loading: false,
			Types: [], TypeSearch: '',
			SelectedID: 0, Selected: null,
			ShowNew: false,
			Def: { Name: '', Description: '', Hash: '', Ordinal: 0 }, HashTouched: false, HashChangeConfirmed: false,
			Statuses: [], DefaultDates: [], ShowDateCodes: false,
			AllowedProjectTypeIDs: [],
			DefaultFunding: [],
			Prototype: null, DetailLoading: false,
			Saving: false, Notice: '', Error: '',
			DeleteConfirm: false, Deleting: false,
		};
	}

	/** Set the active kind (called from each view's onAfterRender), ensure loaded, render. */
	activate(pKind)
	{
		const tmpRoot = this.root;
		tmpRoot.ActiveKind = (pKind === 'Contract') ? 'Contract' : 'Project';
		this.render();
		const tmpSlice = this.state;
		if (!tmpSlice.Loaded && !tmpSlice.Loading) { this.loadAsync(); }
		this.mountPickers();
	}

	/** Deep-link entry (#/ProjectTypes/:ID): activate the kind then select the type — deferring until the rail loads. */
	openType(pKind, pID)
	{
		this.activate(pKind);
		if (String(pID).toLowerCase() === 'new') { this.showNew(); return; }   // #/{Project,Contract}Types/New → create form
		const tmpID = Number(pID) || 0;
		if (!tmpID) { return; }
		const tmpSlice = this.state;
		if (tmpSlice.Loaded) { this.selectType(tmpID); } else { tmpSlice.PendingSelectID = tmpID; }
	}

	// ── REST helpers ─────────────────────────────────────────────────────────────
	_getJSON(pURL) { const tmpRC = this.restClient; return tmpRC ? new Promise((resolve) => tmpRC.getJSON(pURL, (pErr, pBody) => resolve(pErr ? null : pBody))) : Promise.resolve(null); }
	_getEntitySet(pEntity, pFilter, pCount) { const tmpRC = this.restClient; return tmpRC ? new Promise((resolve) => tmpRC.getEntitySet(pEntity, pFilter, (pErr, pRows) => resolve((!pErr && Array.isArray(pRows)) ? pRows : []), 0, pCount || 2000)) : Promise.resolve([]); }
	_upsertEntity(pEntity, pRecord) { const tmpRC = this.restClient; return tmpRC ? new Promise((resolve) => tmpRC.upsertEntity(pEntity, pRecord, (pErr, pRes) => resolve({ error: pErr, result: pRes }))) : Promise.resolve({ error: 'no client' }); }
	_deleteEntity(pEntity, pID) { const tmpRC = this.restClient; return (tmpRC && (typeof tmpRC.deleteEntity === 'function')) ? new Promise((resolve) => tmpRC.deleteEntity(pEntity, pID, (pErr) => resolve(!pErr))) : Promise.resolve(false); }
	_esc(pStr) { return String(pStr == null ? '' : pStr).replace(/[&<>"']/g, (pChar) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[pChar])); }

	// ── Load the rail ────────────────────────────────────────────────────────────
	loadAsync()
	{
		const tmpState = this.state;
		const tmpCfg = this.cfg;
		if (tmpState.Loading) { return; }
		tmpState.Loading = true;
		this.render();
		// ProjectType has no Deleted column (ContractType does) — only include the Deleted clause where it exists.
		const tmpFilter = `${tmpCfg.HasDeleted ? 'FBV~Deleted~EQ~0~' : ''}FBV~IDCustomer~EQ~${this.callerCustomer()}`;
		this._getJSON(`${tmpCfg.Plural}/FilteredTo/${tmpFilter}/0/5000`).then((pRows) =>
		{
			const tmpTypes = Array.isArray(pRows) ? pRows.slice() : [];
			tmpTypes.sort((pA, pB) => tmpCfg.HasOrdinal
				? ((Number(pA.Ordinal || 0) - Number(pB.Ordinal || 0)) || String(pA.Name || '').localeCompare(String(pB.Name || '')))
				: String(pA.Name || '').localeCompare(String(pB.Name || '')));
			tmpState.Types = tmpTypes;
			tmpState.Loaded = true;
			tmpState.Loading = false;
			if (tmpState.PendingSelectID) { const tmpPending = tmpState.PendingSelectID; tmpState.PendingSelectID = 0; this.selectType(tmpPending); }
			else { this.render(); }
		});
	}

	_typeLabel(pType) { if (!pType) { return ''; } return String(pType.Name || pType.Hash || `Type ${pType[this.cfg.IDField]}`); }

	setTypeSearch(pValue)
	{
		this.state.TypeSearch = String(pValue == null ? '' : pValue);
		const tmpCA = this.pict.ContentAssignment;
		if (tmpCA.getElement(RAIL_LIST_MOUNT).length > 0) { tmpCA.assignContent(RAIL_LIST_MOUNT, this._railListHTML()); }
		else { this.render(); }
	}

	// ── Select a type + hydrate its detail ───────────────────────────────────────
	selectType(pID)
	{
		const tmpState = this.state;
		const tmpCfg = this.cfg;
		const tmpID = Number(pID);
		tmpState.SelectedID = tmpID;
		tmpState.Selected = (tmpState.Types || []).find((pT) => Number(pT[tmpCfg.IDField]) === tmpID) || null;
		tmpState.ShowNew = false;
		tmpState.Notice = ''; tmpState.Error = ''; tmpState.DeleteConfirm = false;
		tmpState.HashTouched = false; tmpState.HashChangeConfirmed = false;
		const tmpType = tmpState.Selected;
		tmpState.Def = tmpType
			? { Name: String(tmpType.Name || ''), Description: String(tmpType.Description || ''), Hash: String(tmpType.Hash || ''), Ordinal: Number(tmpType.Ordinal || 0) }
			: { Name: '', Description: '', Hash: '', Ordinal: 0 };
		tmpState.Statuses = []; tmpState.DefaultDates = []; tmpState.AllowedProjectTypeIDs = []; tmpState.DefaultFunding = []; tmpState.Prototype = null;
		this.render();
		if (tmpID > 0) { this._loadDetailAsync(tmpID, tmpType); }
	}

	async _loadDetailAsync(pID, pType)
	{
		const tmpState = this.state;
		const tmpCfg = this.cfg;
		tmpState.DetailLoading = true;
		this.render();
		const tmpRead = this._readProvider;
		const [ tmpStatuses, tmpDates, tmpAllowed, tmpProto ] = await Promise.all([
			tmpRead ? tmpRead.loadStatuses(tmpCfg.Entity, pID) : Promise.resolve({ Available: [], RequiresChangeOrder: [] }),
			tmpRead ? tmpRead.loadDefaultDates(tmpCfg.Entity, pID) : Promise.resolve([]),
			(tmpCfg.HasAllowedTypes && tmpRead) ? tmpRead.loadAllowedProjectTypes(pID) : Promise.resolve([]),
			tmpRead ? tmpRead.loadPrototype(tmpCfg.Entity, pType) : Promise.resolve(null),
		]);
		if (Number(tmpState.SelectedID) !== Number(pID)) { return; }
		tmpState.Statuses = (tmpStatuses.Available || []).map((pName) => ({ Name: pName, RequiresChangeOrder: (tmpStatuses.RequiresChangeOrder || []).indexOf(pName) > -1 }));
		tmpState.DefaultDates = (tmpDates || []).map((pRow) => ({ Name: pRow.Name || pRow.Hash || '', Hash: pRow.Hash || '' }));
		tmpState.AllowedProjectTypeIDs = (tmpAllowed || []).map(Number).filter((pV) => pV > 0);
		tmpState.Prototype = tmpProto || null;
		if (tmpCfg.HasFunding) { await this._loadFundingAsync(tmpState, pID); }
		tmpState.DetailLoading = false;
		this.render();
	}

	// getEntitySet appends its own 's' — pass the SINGULAR Scope name. Each row gets a stable Key for its two
	// searchable pickers (source + category); the option sets are fetched on demand by the pickers, not preloaded
	// (FundingSource can be thousands of rows per customer). Writes to the passed slice (kind-switch safe).
	async _loadFundingAsync(pSlice, pIDContractType)
	{
		const tmpRows = await this._getEntitySet('ContractTypeDefaultFunding', `FBV~IDContractType~EQ~${pIDContractType}~FBV~Deleted~EQ~0`, 5000);
		pSlice.DefaultFunding = tmpRows.map((pRow) => ({ Key: ++this._fundKeySeq, IDRow: Number(pRow.IDContractTypeDefaultFunding) || 0, IDFundingSource: Number(pRow.IDFundingSource) || 0, IDFundingCategory: Number(pRow.IDFundingCategory) || 0 }));
	}

	// ── Definition editing ───────────────────────────────────────────────────────
	setDefField(pField, pValue)
	{
		const tmpState = this.state;
		if (pField === 'Ordinal') { tmpState.Def.Ordinal = Number(pValue) || 0; return; }
		if (!Object.prototype.hasOwnProperty.call(tmpState.Def, pField)) { return; }
		tmpState.Def[pField] = String(pValue == null ? '' : pValue);
		if (pField === 'Name' && !tmpState.HashTouched && (!tmpState.Selected)) { tmpState.Def.Hash = libProjectTypeComprehensionBuilder.slug(tmpState.Def.Name || ''); }
	}
	setHash(pValue) { const tmpState = this.state; tmpState.HashTouched = true; tmpState.Def.Hash = String(pValue == null ? '' : pValue); }
	confirmHashChange(pChecked) { this.state.HashChangeConfirmed = !!pChecked; this.render(); }

	// ── Status lifecycle ─────────────────────────────────────────────────────────
	addStatus() { this.state.Statuses.push({ Name: '', RequiresChangeOrder: false }); this._renderDetail(); }
	removeStatus(pIndex) { this.state.Statuses.splice(pIndex, 1); this._renderDetail(); }
	setStatusName(pIndex, pValue) { if (this.state.Statuses[pIndex]) { this.state.Statuses[pIndex].Name = String(pValue == null ? '' : pValue); } }
	toggleStatusCO(pIndex, pChecked) { if (this.state.Statuses[pIndex]) { this.state.Statuses[pIndex].RequiresChangeOrder = !!pChecked; } }
	moveStatus(pIndex, pDir)
	{
		const tmpList = this.state.Statuses; const tmpTo = pIndex + pDir;
		if (tmpTo < 0 || tmpTo >= tmpList.length) { return; }
		const tmpTmp = tmpList[pIndex]; tmpList[pIndex] = tmpList[tmpTo]; tmpList[tmpTo] = tmpTmp;
		this._renderDetail();
	}

	// ── Default dates ────────────────────────────────────────────────────────────
	addDefaultDate() { this.state.DefaultDates.push({ Name: '', Hash: '' }); this._renderDetail(); }
	removeDefaultDate(pIndex) { this.state.DefaultDates.splice(pIndex, 1); this._renderDetail(); }
	setDateName(pIndex, pValue)
	{
		const tmpRow = this.state.DefaultDates[pIndex];
		if (!tmpRow) { return; }
		tmpRow.Name = String(pValue == null ? '' : pValue);
		if (!tmpRow.Hash) { tmpRow.Hash = libProjectTypeComprehensionBuilder.slug(tmpRow.Name || ''); }   // freeze on first fill so renames update in place
	}
	setDateHash(pIndex, pValue) { const tmpRow = this.state.DefaultDates[pIndex]; if (tmpRow) { tmpRow.Hash = String(pValue == null ? '' : pValue); } }
	toggleDateCodes() { this.state.ShowDateCodes = !this.state.ShowDateCodes; this._renderDetail(); }

	// ── Default funding (ContractType) — searchable pickers per row, keyed by a stable row Key ───
	addFunding() { this.state.DefaultFunding.push({ Key: ++this._fundKeySeq, IDRow: 0, IDFundingSource: 0, IDFundingCategory: 0 }); this._renderDetail(); }
	removeFundingByKey(pKey) { this.state.DefaultFunding = (this.state.DefaultFunding || []).filter((pRow) => pRow.Key !== pKey); this._renderDetail(); }
	setFundingFieldByKey(pKey, pField, pValue) { const tmpRow = (this.state.DefaultFunding || []).find((pRow) => pRow.Key === pKey); if (tmpRow && (pField === 'IDFundingSource' || pField === 'IDFundingCategory')) { tmpRow[pField] = Number(pValue) || 0; } }

	// ── New type ─────────────────────────────────────────────────────────────────
	showNew()
	{
		const tmpState = this.state;
		tmpState.ShowNew = true; tmpState.SelectedID = 0; tmpState.Selected = null;
		tmpState.Def = { Name: '', Description: '', Hash: '', Ordinal: 0 }; tmpState.HashTouched = false;
		tmpState.Statuses = []; tmpState.DefaultDates = []; tmpState.AllowedProjectTypeIDs = []; tmpState.DefaultFunding = []; tmpState.Prototype = null;
		tmpState.Notice = ''; tmpState.Error = '';
		this.render();
	}
	cancelNew() { this.state.ShowNew = false; this.render(); }

	// ── Save (build the editor record → reuse TypeComprehensionSaveProvider) ─────
	async saveAsync()
	{
		const tmpState = this.state;
		const tmpCfg = this.cfg;
		if (!this.canEditDef() || tmpState.Saving) { return; }
		const tmpIsCreate = !tmpState.Selected;
		const tmpName = String(tmpState.Def.Name || '').trim();
		if (!tmpName) { tmpState.Error = 'Give the type a name.'; tmpState.Notice = ''; this.render(); return; }
		const tmpHash = String(tmpState.Def.Hash || '').trim() || libProjectTypeComprehensionBuilder.slug(tmpName);
		// status name safety — the reuse path round-trips names through a comma-joined string
		if (tmpState.Statuses.some((pS) => String(pS.Name || '').indexOf(',') > -1)) { tmpState.Error = 'Status names cannot contain commas.'; tmpState.Notice = ''; this.render(); return; }
		tmpState.Saving = true; tmpState.Error = ''; tmpState.Notice = '';
		this.render();

		const tmpStatusesClean = tmpState.Statuses.map((pS) => ({ Name: String(pS.Name || '').trim(), RequiresChangeOrder: !!pS.RequiresChangeOrder })).filter((pS) => pS.Name);
		const tmpDatesClean = tmpState.DefaultDates.map((pD) => ({ Name: String(pD.Name || '').trim(), Hash: String(pD.Hash || '').trim() || libProjectTypeComprehensionBuilder.slug(String(pD.Name || '').trim()) })).filter((pD) => pD.Name || pD.Hash);
		const tmpSelected = tmpState.Selected || {};
		const tmpRecord = Object.assign({}, tmpSelected, {
			Name: tmpName, Description: String(tmpState.Def.Description || ''), Hash: tmpHash,
			IDCustomer: tmpSelected.IDCustomer || this.callerCustomer(),
			Settings: this._readProvider ? this._readProvider.parseSettings(tmpSelected) : {},
			_DisplaySettings: {
				AvailableStatusList: tmpStatusesClean.map((pS) => pS.Name).join(','),
				StatusesRequiringChangeOrder: tmpStatusesClean.filter((pS) => pS.RequiresChangeOrder).map((pS) => pS.Name).join(','),
				DefaultDates: tmpDatesClean,
			},
		});
		if (tmpCfg.HasOrdinal) { tmpRecord.Ordinal = Number(tmpState.Def.Ordinal) || 0; tmpRecord.IDProgram = tmpSelected.IDProgram || 0; }
		if (!tmpIsCreate) { tmpRecord[tmpCfg.GUIDField] = tmpSelected[tmpCfg.GUIDField]; }
		if (tmpCfg.HasAllowedTypes) { tmpRecord._DisplaySettings.AllowedProjectTypeIDs = (tmpState.AllowedProjectTypeIDs || []).map(Number).filter((pV) => pV > 0).join(','); }

		const tmpSave = this._saveProvider;
		let tmpSaved = null;
		try { tmpSaved = tmpSave ? await tmpSave[tmpCfg.SaveMethod](tmpRecord, tmpIsCreate) : null; }
		catch (pErr) { tmpState.Saving = false; tmpState.Error = 'The server rejected the save.'; this.render(); return; }
		if (!tmpSaved || !tmpSaved[tmpCfg.IDField]) { tmpState.Saving = false; tmpState.Error = 'The server rejected the save.'; this.render(); return; }

		// Reconcile funding against the slice saveAsync captured (NOT this.state) — the save's multi-second round-trips
		// mean the user may have navigated to the other kind, flipping this.state to the wrong (empty) slice.
		if (tmpCfg.HasFunding) { await this._reconcileFundingAsync(tmpState, Number(tmpSaved[tmpCfg.IDField])); }

		// merge/insert the saved type into the (captured) slice's rail
		const tmpIdx = tmpState.Types.findIndex((pT) => Number(pT[tmpCfg.IDField]) === Number(tmpSaved[tmpCfg.IDField]));
		if (tmpIdx > -1) { tmpState.Types[tmpIdx] = tmpSaved; } else { tmpState.Types.push(tmpSaved); }
		tmpState.Types.sort((pA, pB) => tmpCfg.HasOrdinal ? ((Number(pA.Ordinal || 0) - Number(pB.Ordinal || 0)) || String(pA.Name || '').localeCompare(String(pB.Name || ''))) : String(pA.Name || '').localeCompare(String(pB.Name || '')));
		tmpState.Saving = false;
		tmpState.ShowNew = false;
		// If the user switched to the other kind during the save, it already persisted — don't reselect/notify the wrong slice.
		if (this.root.ActiveKind !== tmpCfg.Kind) { return; }
		this.selectType(Number(tmpSaved[tmpCfg.IDField]));   // reselect + re-hydrate (clears Notice)…
		this.state.Notice = 'Saved.';                        // …so set the confirmation after, and repaint
		this.render();
	}

	/** Diff-reconcile the ContractType default-funding rows (plain Meadow CRUD; the save provider doesn't touch them).
	 * Operates on the passed slice (kind-switch safe). */
	async _reconcileFundingAsync(pSlice, pIDContractType)
	{
		const tmpExisting = await this._getEntitySet('ContractTypeDefaultFunding', `FBV~IDContractType~EQ~${pIDContractType}~FBV~Deleted~EQ~0`, 5000);
		const tmpExistingByID = {};
		tmpExisting.forEach((pRow) => { tmpExistingByID[Number(pRow.IDContractTypeDefaultFunding)] = pRow; });
		const tmpKeep = {};
		for (const tmpRow of pSlice.DefaultFunding)
		{
			if (!(tmpRow.IDFundingSource > 0) && !(tmpRow.IDFundingCategory > 0)) { continue; }
			const tmpBody = { IDContractTypeDefaultFunding: tmpRow.IDRow || 0, IDFundingSource: tmpRow.IDFundingSource || 0, IDFundingCategory: tmpRow.IDFundingCategory || 0, IDContractType: pIDContractType, IDCustomer: this.callerCustomer() };
			/* eslint-disable no-await-in-loop */
			const tmpRes = await this._upsertEntity('ContractTypeDefaultFunding', tmpBody);
			/* eslint-enable no-await-in-loop */
			const tmpSavedID = (tmpRes.result && Number(tmpRes.result.IDContractTypeDefaultFunding)) || tmpRow.IDRow;
			if (tmpSavedID) { tmpKeep[tmpSavedID] = true; }
		}
		for (const tmpID of Object.keys(tmpExistingByID)) { if (!tmpKeep[tmpID]) { /* eslint-disable-next-line no-await-in-loop */ await this._deleteEntity('ContractTypeDefaultFunding', Number(tmpID)); } }
	}

	// ── Delete ───────────────────────────────────────────────────────────────────
	askDelete() { this.state.DeleteConfirm = true; this.render(); }
	cancelDelete() { this.state.DeleteConfirm = false; this.render(); }
	async deleteTypeAsync()
	{
		const tmpState = this.state;
		const tmpCfg = this.cfg;
		if (!this.canDelete() || tmpState.Deleting || !tmpState.Selected) { return; }
		tmpState.Deleting = true;
		this.render();
		const tmpID = Number(tmpState.SelectedID);
		const tmpOK = await this._deleteEntity(tmpCfg.Entity, tmpID);
		tmpState.Deleting = false; tmpState.DeleteConfirm = false;
		if (!tmpOK) { tmpState.Error = 'The server refused to delete this type (you may lack permission, or it is in use).'; this.render(); return; }
		tmpState.Types = tmpState.Types.filter((pT) => Number(pT[tmpCfg.IDField]) !== tmpID);
		tmpState.SelectedID = 0; tmpState.Selected = null;
		this.render();
	}

	// ── Prototype panel ──────────────────────────────────────────────────────────
	/** Record where we came from (for the return banner) and deep-link into a prototype editor. */
	openProtoStep(pStepKey)
	{
		const tmpState = this.state;
		const tmpCfg = this.cfg;
		const tmpProto = tmpState.Prototype;
		const tmpProtoID = tmpProto ? Number(tmpProto[tmpCfg.ProtoIDField]) : 0;
		let tmpTarget = null;
		if (pStepKey === 'open')
		{
			if (!tmpProtoID) { return; }
			tmpTarget = `${tmpCfg.ProtoTab}/${tmpProtoID}`;
		}
		else
		{
			const tmpStep = PROTO_STEPS.find((pS) => pS.Key === pStepKey);
			if (!tmpStep) { return; }
			if ((tmpStep.Scope === 'prototype') && !tmpProtoID) { return; }
			tmpTarget = tmpStep.route(tmpCfg, tmpProtoID);
		}
		this.pict.AppData.PrototypeSetupContext = {
			Entity: tmpCfg.Kind, IDType: tmpState.SelectedID, TypeName: this._typeLabel(tmpState.Selected),
			IDPrototype: tmpProtoID, ReturnHash: `${tmpCfg.Route}/${tmpState.SelectedID}`,
		};
		window.location.hash = tmpTarget;
	}

	/** Create the prototype for a type that has none yet: re-save through the canonical path (saveAsync mints the
	 * prototype shell + writes the pointer, then reselects, which re-hydrates the prototype). */
	async setupPrototypeAsync()
	{
		if (!this.canEditDef() || !this.state.Selected || this.state.Saving) { return; }
		await this.saveAsync();
	}

	// ── Pickers ──────────────────────────────────────────────────────────────────
	mountPickers()
	{
		const tmpCfg = this.cfg;
		const tmpPicker = this.pict.providers['Pict-Section-Picker'];
		if (!tmpPicker || (typeof tmpPicker.createPicker !== 'function')) { return; }
		const tmpCA = this.pict.ContentAssignment;
		if (tmpCfg.HasAllowedTypes && (tmpCA.getElement(ALLOWED_PICKER_MOUNT).length > 0))
		{
			const tmpP = tmpPicker.createPicker('PCT-AllowedPicker',
				{
					DestinationAddress: ALLOWED_PICKER_MOUNT, ValueAddress: 'AppData.ProjectContractTypes.Contract.AllowedProjectTypeIDs',
					Mode: 'multi', Searchable: true, Placeholder: 'Search project types…',
					DataProvider: this._allowedDataProvider(), ResolveValue: this._allowedResolveValue(),
				});
			if (tmpP && (typeof tmpP.render === 'function')) { tmpP.render(); }
			// Push the loaded value in explicitly so the picker seeds chip labels via ResolveValue (a re-mount alone
			// doesn't re-read ValueAddress for pre-set values); _seedSelectedRecords is idempotent per value.
			if (tmpP && (typeof tmpP.setValue === 'function')) { tmpP.setValue((this.state.AllowedProjectTypeIDs || []).slice()); }
		}
		if (tmpCfg.HasFunding) { this._mountFundingPickers(tmpPicker, tmpCA); }
	}
	_allowedDataProvider()
	{
		const tmpPageSize = 50;
		return (pTerm) =>
		{
			const tmpTerm = String(pTerm == null ? '' : pTerm).trim();
			const tmpScope = `FBV~IDCustomer~EQ~${this.callerCustomer()}`;   // ProjectType has no Deleted column
			const fMap = (pRows) => (Array.isArray(pRows) ? pRows : []).map((pRec) => ({ Value: Number(pRec.IDProjectType), Text: pRec.Name || `#${pRec.IDProjectType}`, Record: pRec }));
			if (!tmpTerm) { return this._getJSON(`ProjectTypes/FilteredTo/${tmpScope}/0/${tmpPageSize}`).then((pRows) => ({ results: fMap(pRows), hasMore: false })); }
			const tmpLike = encodeURIComponent(`%${tmpTerm}%`);
			return this._getJSON(`ProjectTypes/FilteredTo/FBV~Name~LK~${tmpLike}~${tmpScope}/0/${tmpPageSize}`).then((pRows) => ({ results: fMap(pRows), hasMore: false }));
		};
	}
	_allowedResolveValue()
	{
		return (pValue) => { const tmpID = Number(pValue); if (!tmpID) { return Promise.resolve(null); } return this._getJSON(`ProjectType/${tmpID}`).then((pRec) => ((pRec && pRec.IDProjectType) ? { Value: tmpID, Text: pRec.Name || `#${tmpID}` } : { Value: tmpID, Text: `#${tmpID}` })); };
	}

	// Two searchable pickers per funding row (source + category), keyed by the row's stable Key. Uses OnChange (not a
	// ValueAddress) since the value lives at a dynamic array index; setValue() seeds the current label.
	_FUND_DEFS()
	{
		return [
			{ Field: 'IDFundingSource', Singular: 'FundingSource', Plural: 'FundingSources', Phrase: 'funding sources', One: 'funding source', HashPrefix: 'PCT-FundSrc-' },
			{ Field: 'IDFundingCategory', Singular: 'FundingCategory', Plural: 'FundingCategories', Phrase: 'funding categories', One: 'funding category', HashPrefix: 'PCT-FundCat-' },
		];
	}
	_mountFundingPickers(pPicker, pCA)
	{
		(this.state.DefaultFunding || []).forEach((pRow) =>
		{
			this._FUND_DEFS().forEach((pDef) =>
			{
				const tmpHash = `${pDef.HashPrefix}${pRow.Key}`;
				if (pCA.getElement(`#${tmpHash}`).length === 0) { return; }
				const tmpKey = pRow.Key;
				const tmpP = pPicker.createPicker(tmpHash,
					{
						DestinationAddress: `#${tmpHash}`, Searchable: true, AllowClear: true, ClearLabel: `— ${pDef.One} —`,
						Placeholder: `Search ${pDef.Phrase}…`,
						DataProvider: this._fundingDataProvider(pDef.Plural, pDef.Field),
						ResolveValue: this._fundingResolveValue(pDef.Singular),
						OnChange: (pValue) => this.setFundingFieldByKey(tmpKey, pDef.Field, pValue),
					});
				if (tmpP && (typeof tmpP.render === 'function')) { tmpP.render(); }
				if (tmpP && (typeof tmpP.setValue === 'function')) { tmpP.setValue(pRow[pDef.Field] > 0 ? pRow[pDef.Field] : ''); }
			});
		});
	}
	_fundingDataProvider(pEntityPlural, pIDField)
	{
		return libEntityPickerSearch.entityPickerDataProvider((pURL) => this._getJSON(pURL),
			{
				Set: pEntityPlural, IDField: pIDField, Scope: () => `FBV~IDCustomer~EQ~${this.callerCustomer()}~FBV~Deleted~EQ~0`,
				ToOption: (pRec) => ({ Value: Number(pRec[pIDField]), Text: pRec.Name || '(unnamed)', Record: pRec }),
			});
	}
	_fundingResolveValue(pEntitySingular)
	{
		const tmpIDField = `ID${pEntitySingular}`;
		return (pValue) => { const tmpID = Number(pValue); if (!tmpID) { return Promise.resolve(null); } return this._getJSON(`${pEntitySingular}/${tmpID}`).then((pRec) => ((pRec && pRec[tmpIDField]) ? { Value: tmpID, Text: pRec.Name || `#${tmpID}` } : { Value: tmpID, Text: `#${tmpID}` })); };
	}

	// ── Render ───────────────────────────────────────────────────────────────────
	render()
	{
		const tmpCA = this.pict.ContentAssignment;
		if (tmpCA.getElement(RAIL_MOUNT).length === 0) { return; }
		if (tmpCA.getElement(RAIL_LIST_MOUNT).length === 0) { tmpCA.assignContent(RAIL_MOUNT, this._railHTML()); }
		else { tmpCA.assignContent(RAIL_LIST_MOUNT, this._railListHTML()); }
		tmpCA.assignContent(DETAIL_MOUNT, this._detailHTML());
		this.mountPickers();
	}
	_renderDetail()
	{
		const tmpCA = this.pict.ContentAssignment;
		if (tmpCA.getElement(DETAIL_MOUNT).length > 0) { tmpCA.assignContent(DETAIL_MOUNT, this._detailHTML()); this.mountPickers(); }
		else { this.render(); }
	}

	_railHTML()
	{
		const tmpState = this.state;
		const tmpCfg = this.cfg;
		if (!tmpState.Loaded) { return '<div class="pct-empty">Loading…</div>'; }
		const tmpNew = this.canCreate() ? `<button class="pct-newtype-btn" onclick="_Pict.providers.ProjectContractTypes.showNew()"><i class="fas fa-plus"></i> New ${this._esc(tmpCfg.Label)}</button>` : '';
		return `<input class="pct-rail-search" type="text" autocomplete="off" spellcheck="false" placeholder="Search ${this._esc(tmpCfg.LabelPlural.toLowerCase())}…" value="${this._esc(tmpState.TypeSearch || '')}" oninput="_Pict.providers.ProjectContractTypes.setTypeSearch(this.value)" />`
			+ tmpNew
			+ `<div id="PCT-Rail-List" class="pct-rail-list">${this._railListHTML()}</div>`;
	}
	_railListHTML()
	{
		const tmpState = this.state;
		const tmpCfg = this.cfg;
		const tmpQuery = String(tmpState.TypeSearch || '').trim().toLowerCase();
		const tmpTypes = (tmpState.Types || []).filter((pT) => !tmpQuery
			|| String(this._typeLabel(pT)).toLowerCase().indexOf(tmpQuery) > -1
			|| String(pT.Hash || '').toLowerCase().indexOf(tmpQuery) > -1
			|| String(pT.Description || '').toLowerCase().indexOf(tmpQuery) > -1);
		if (!tmpTypes.length) { return `<div class="pct-empty">${tmpQuery ? 'No matches.' : `No ${this._esc(tmpCfg.LabelPlural.toLowerCase())} yet.`}</div>`; }
		return tmpTypes.map((pT) =>
		{
			const tmpID = Number(pT[tmpCfg.IDField]);
			const tmpActive = (Number(tmpState.SelectedID) === tmpID) ? ' is-active' : '';
			return `<div class="pct-rail-item${tmpActive}" onclick="_Pict.providers.ProjectContractTypes.selectType(${tmpID})"><div class="pct-rail-name">${this._esc(this._typeLabel(pT))}</div>${pT.Description ? `<div class="pct-rail-sub">${this._esc(String(pT.Description).slice(0, 60))}</div>` : ''}</div>`;
		}).join('');
	}

	_detailHTML()
	{
		const tmpState = this.state;
		const tmpCfg = this.cfg;
		if (tmpState.ShowNew) { return this._formHTML(true); }
		if (!tmpState.SelectedID || !tmpState.Selected) { return `<div class="pct-detail-empty">Pick a ${this._esc(tmpCfg.Label)} on the left to edit it and set up its template — or create a new one.</div>`; }
		return `<div class="pct-detail-head"><div class="pct-detail-title">${this._esc(this._typeLabel(tmpState.Selected))}</div>`
			+ (tmpState.Selected.Hash ? `<div class="pct-detail-sub">${this._esc(tmpState.Selected.Hash)}</div>` : '') + `</div>`
			+ this._prototypeHTML()
			+ this._formHTML(false);
	}

	_prototypeHTML()
	{
		const tmpState = this.state;
		const tmpCfg = this.cfg;
		const tmpProto = tmpState.Prototype;
		if (tmpState.DetailLoading && !tmpProto) { return `<div class="pct-section"><div class="pct-section-head">Template (prototype ${this._esc(tmpCfg.Kind.toLowerCase())})</div><p class="pct-note">Loading…</p></div>`; }
		if (!tmpProto)
		{
			const tmpBtn = this.canEditDef() ? `<button class="pct-btn pct-btn-primary" onclick="_Pict.providers.ProjectContractTypes.setupPrototypeAsync()">Set up prototype</button>` : '';
			return `<div class="pct-section"><div class="pct-section-head">Template (prototype ${this._esc(tmpCfg.Kind.toLowerCase())})</div>`
				+ `<p class="pct-note">This type has no prototype ${this._esc(tmpCfg.Kind.toLowerCase())} yet — the template new ${this._esc(tmpCfg.Kind.toLowerCase())}s are cloned from. Create it, then configure its dates, labels, observation types and more.</p>${tmpBtn}</div>`;
		}
		const tmpProtoName = String(tmpProto.Name || `${tmpCfg.ProtoEntity} ${tmpProto[tmpCfg.ProtoIDField]}`);
		const tmpSteps = PROTO_STEPS.filter((pStep) => pStep.Kinds.indexOf(tmpCfg.Kind) > -1).map((pStep) =>
		{
			const tmpOrg = (pStep.Scope === 'org') ? '<span class="pct-org">org-wide</span>' : '';
			const tmpGate = (pStep.Scope === 'prototype') && !this.canEditProto();
			return `<button class="pct-step${tmpGate ? ' is-disabled' : ''}"${tmpGate ? ' disabled title="You do not have permission to edit projects."' : ''} onclick="_Pict.providers.ProjectContractTypes.openProtoStep('${pStep.Key}')"><span class="pct-step-label">${this._esc(pStep.Label)}</span>${tmpOrg}</button>`;
		}).join('');
		const tmpOpen = `<button class="pct-btn pct-btn-ghost" onclick="_Pict.providers.ProjectContractTypes.openProtoStep('open')">Open prototype ${this._esc(tmpCfg.Kind.toLowerCase())} →</button>`;
		return `<div class="pct-section"><div class="pct-section-head">Template (prototype ${this._esc(tmpCfg.Kind.toLowerCase())})</div>`
			+ `<p class="pct-note">New ${this._esc(tmpCfg.Kind.toLowerCase())}s of this type are cloned from <b>${this._esc(tmpProtoName)}</b>. Configure the template below — each opens the real editor for the prototype; use the banner at the top to come back.</p>`
			+ `<div class="pct-steps">${tmpSteps}</div><div class="pct-add-row">${tmpOpen}</div></div>`;
	}

	_formHTML(pIsNew)
	{
		const tmpState = this.state;
		const tmpCfg = this.cfg;
		const tmpEditable = pIsNew ? this.canCreate() : this.canEditDef();
		if (!tmpEditable && pIsNew) { return ''; }
		const tmpDis = tmpEditable ? '' : ' disabled';
		const tmpNotice = tmpState.Notice ? `<div class="pct-notice">${this._esc(tmpState.Notice)}</div>` : '';
		const tmpError = tmpState.Error ? `<div class="pct-error">${this._esc(tmpState.Error)}</div>` : '';
		const tmpBusy = tmpState.Saving ? '<span class="pct-busy">saving…</span>' : '';
		const tmpHead = pIsNew ? `<div class="pct-detail-head"><div class="pct-detail-title">New ${this._esc(tmpCfg.Label)}</div></div>` : '';
		return tmpHead
			+ this._definitionHTML(tmpDis, pIsNew)
			+ this._statusesHTML(tmpEditable)
			+ this._datesHTML(tmpEditable)
			+ (tmpCfg.HasAllowedTypes ? this._allowedHTML(tmpEditable) : '')
			+ (tmpCfg.HasFunding ? this._fundingHTML(tmpEditable) : '')
			+ (tmpEditable ? `<div class="pct-add-row"><button class="pct-btn pct-btn-primary" onclick="_Pict.providers.ProjectContractTypes.saveAsync()">Save ${this._esc(tmpCfg.Label)}</button>${pIsNew ? `<button class="pct-btn pct-btn-ghost" onclick="_Pict.providers.ProjectContractTypes.cancelNew()">Cancel</button>` : ''}${tmpBusy}</div>` : '<p class="pct-note">You can view this type, but editing needs the Update permission.</p>')
			+ tmpNotice + tmpError
			+ ((!pIsNew && this.canDelete()) ? this._dangerHTML() : '');
	}

	_definitionHTML(pDis, pIsNew)
	{
		const tmpState = this.state;
		const tmpCfg = this.cfg;
		const tmpHashWarn = (!pIsNew && !tmpState.HashChangeConfirmed)
			? `<div class="pct-hint"><i class="fas fa-triangle-exclamation"></i> The short code is a natural key — imports and a contract type's allowed-types list match on it. <label><input type="checkbox" onchange="_Pict.providers.ProjectContractTypes.confirmHashChange(this.checked)"> Yes, let me change it</label></div>`
			: '';
		const tmpHashDis = pDis || ((!pIsNew && !tmpState.HashChangeConfirmed) ? ' disabled' : '');
		const tmpOrdinal = tmpCfg.HasOrdinal ? `<label class="pct-field pct-field-inline"><span>Display order</span><input type="number" step="1"${pDis} value="${Number(tmpState.Def.Ordinal) || 0}" onchange="_Pict.providers.ProjectContractTypes.setDefField('Ordinal', this.value)"></label>` : '';
		return `<div class="pct-section"><div class="pct-section-head">Definition</div>`
			+ `<label class="pct-field"><span>Name</span><input type="text"${pDis} value="${this._esc(tmpState.Def.Name)}" oninput="_Pict.providers.ProjectContractTypes.setDefField('Name', this.value)"></label>`
			+ `<label class="pct-field"><span>Description</span><textarea rows="2"${pDis} oninput="_Pict.providers.ProjectContractTypes.setDefField('Description', this.value)">${this._esc(tmpState.Def.Description)}</textarea></label>`
			+ tmpOrdinal
			+ `<details class="pct-advanced"><summary>Advanced</summary><label class="pct-field"><span>Short code (hash)</span><input type="text"${tmpHashDis} value="${this._esc(tmpState.Def.Hash)}" oninput="_Pict.providers.ProjectContractTypes.setHash(this.value)"></label>${tmpHashWarn}</details>`
			+ `</div>`;
	}

	_statusesHTML(pEditable)
	{
		const tmpState = this.state;
		const tmpRows = (tmpState.Statuses || []).map((pS, pIdx) =>
			`<div class="pct-status-row"><input class="pct-status-name" type="text"${pEditable ? '' : ' disabled'} value="${this._esc(pS.Name)}" oninput="_Pict.providers.ProjectContractTypes.setStatusName(${pIdx}, this.value)" placeholder="Status name">`
			+ `<label class="pct-status-co"><input type="checkbox"${pS.RequiresChangeOrder ? ' checked' : ''}${pEditable ? '' : ' disabled'} onchange="_Pict.providers.ProjectContractTypes.toggleStatusCO(${pIdx}, this.checked)"> Requires a change order</label>`
			+ (pEditable ? `<span class="pct-row-tools"><button class="pct-icon" title="Move up" onclick="_Pict.providers.ProjectContractTypes.moveStatus(${pIdx}, -1)">↑</button><button class="pct-icon" title="Move down" onclick="_Pict.providers.ProjectContractTypes.moveStatus(${pIdx}, 1)">↓</button><button class="pct-icon pct-icon-x" title="Remove" onclick="_Pict.providers.ProjectContractTypes.removeStatus(${pIdx})">✕</button></span>` : '')
			+ `</div>`).join('');
		const tmpAdd = pEditable ? `<button class="pct-btn pct-btn-ghost pct-btn-sm" onclick="_Pict.providers.ProjectContractTypes.addStatus()">+ Add status</button>` : '';
		return `<div class="pct-section"><div class="pct-section-head">Statuses (lifecycle)</div>`
			+ `<p class="pct-note">The stages a ${this._esc(this.cfg.Kind.toLowerCase())} moves through. Tick the ones that require a change order to enter.</p>`
			+ (tmpRows || '<p class="pct-note">No statuses yet.</p>') + tmpAdd + `</div>`;
	}

	_datesHTML(pEditable)
	{
		const tmpState = this.state;
		const tmpRows = (tmpState.DefaultDates || []).map((pD, pIdx) =>
			`<div class="pct-date-row"><input class="pct-date-name" type="text"${pEditable ? '' : ' disabled'} value="${this._esc(pD.Name)}" oninput="_Pict.providers.ProjectContractTypes.setDateName(${pIdx}, this.value)" placeholder="e.g. Notice to Proceed">`
			+ (tmpState.ShowDateCodes ? `<input class="pct-date-code" type="text"${pEditable ? '' : ' disabled'} value="${this._esc(pD.Hash)}" oninput="_Pict.providers.ProjectContractTypes.setDateHash(${pIdx}, this.value)" placeholder="code">` : '')
			+ (pEditable ? `<button class="pct-icon pct-icon-x" title="Remove" onclick="_Pict.providers.ProjectContractTypes.removeDefaultDate(${pIdx})">✕</button>` : '')
			+ `</div>`).join('');
		const tmpTools = pEditable ? `<div class="pct-add-row"><button class="pct-btn pct-btn-ghost pct-btn-sm" onclick="_Pict.providers.ProjectContractTypes.addDefaultDate()">+ Add date</button><button class="pct-btn pct-btn-ghost pct-btn-sm" onclick="_Pict.providers.ProjectContractTypes.toggleDateCodes()">${tmpState.ShowDateCodes ? 'Hide' : 'Show'} agency codes</button></div>` : '';
		return `<div class="pct-section"><div class="pct-section-head">${this._esc(this.cfg.DefaultDatesLabel)}</div>`
			+ `<p class="pct-note">The milestone dates every ${this._esc(this.cfg.Kind.toLowerCase())} of this type should track. Their values are filled in per ${this._esc(this.cfg.Kind.toLowerCase())}.</p>`
			+ (tmpRows || '<p class="pct-note">No default dates yet.</p>') + tmpTools + `</div>`;
	}

	_allowedHTML(pEditable)
	{
		return `<div class="pct-section"><div class="pct-section-head">Allowed project types</div>`
			+ `<p class="pct-note">Which project types can live under a contract of this type. Leave empty to allow any.</p>`
			+ `<div id="PCT-AllowedPicker" class="pct-picker"${pEditable ? '' : ' style="pointer-events:none;opacity:0.6"'}></div></div>`;
	}

	_fundingHTML(pEditable)
	{
		const tmpState = this.state;
		const tmpRows = (tmpState.DefaultFunding || []).map((pRow) =>
			`<div class="pct-fund-row"><div id="PCT-FundSrc-${pRow.Key}" class="pct-fund-pick"></div><div id="PCT-FundCat-${pRow.Key}" class="pct-fund-pick"></div>`
			+ (pEditable ? `<button class="pct-icon pct-icon-x" title="Remove" onclick="_Pict.providers.ProjectContractTypes.removeFundingByKey(${pRow.Key})">✕</button>` : '')
			+ `</div>`).join('');
		const tmpAdd = pEditable ? `<button class="pct-btn pct-btn-ghost pct-btn-sm" onclick="_Pict.providers.ProjectContractTypes.addFunding()">+ Add funding</button>` : '';
		return `<div class="pct-section"><div class="pct-section-head">Default funding</div>`
			+ `<p class="pct-note">Funding sources &amp; categories seeded onto new contracts of this type.</p>`
			+ (tmpRows || '<p class="pct-note">No default funding yet.</p>') + tmpAdd + `</div>`;
	}

	_dangerHTML()
	{
		const tmpState = this.state;
		const tmpCfg = this.cfg;
		if (tmpState.DeleteConfirm)
		{
			const tmpBusy = tmpState.Deleting ? '<span class="pct-busy">deleting…</span>' : '';
			return `<div class="pct-section pct-danger"><div class="pct-section-head">Delete</div>`
				+ `<p class="pct-note">Delete “${this._esc(this._typeLabel(tmpState.Selected))}”? The prototype ${this._esc(tmpCfg.Kind.toLowerCase())} and default-date rows are not removed.</p>`
				+ `<div class="pct-add-row"><button class="pct-btn pct-btn-danger" onclick="_Pict.providers.ProjectContractTypes.deleteTypeAsync()">Delete ${this._esc(tmpCfg.Label)}</button><button class="pct-btn pct-btn-ghost" onclick="_Pict.providers.ProjectContractTypes.cancelDelete()">Cancel</button>${tmpBusy}</div></div>`;
		}
		return `<div class="pct-section pct-danger"><button class="pct-btn pct-btn-ghost pct-btn-danger-link" onclick="_Pict.providers.ProjectContractTypes.askDelete()"><i class="fas fa-trash-can"></i> Delete this type</button></div>`;
	}
}

module.exports = ProjectContractTypesProvider;
module.exports.default_configuration =
{
	ProviderIdentifier: 'ProjectContractTypes',
	AutoInitialize: true,
	AutoInitializeOrdinal: 43,
};
